/**
 * Shared RTK Query base.
 *
 * Each feature creates its own `createApi` and passes this `baseQuery` so they
 * all share the same auth-aware HTTP client and 401 → refresh → retry semantics.
 *
 * Access token comes from Redux state (memory only — never localStorage).
 * Refresh is silent and rides on the httpOnly `pm_refresh` cookie the API set
 * at login. `credentials: 'include'` is required so the cookie flows.
 *
 * Concurrent 401s share a single inflight refresh via a module-level promise
 * so we never hammer `/refresh` with parallel calls.
 */
import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { env } from '@/lib/env';
import type { AppState } from '@/lib/redux/store';

// Local import path string — we resolve the slice's setters lazily inside the
// query so we don't introduce a circular import with the store.
import { authActions } from '@/features/auth/store/authSlice';
import { markReachable, markRequired, markUnreachable } from '@/shared/lib/errors';

import { refreshAccessToken } from './refreshToken';

const API_PREFIX = '/api/v1';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${env.API_URL}${API_PREFIX}`,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as AppState).auth.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  // ---- API reachability tracking -------------------------------------
  // RTK Query maps `ERR_CONNECTION_REFUSED`, TCP resets, and DNS misses
  // to `error.status === 'FETCH_ERROR'`. If we see that AND the browser
  // itself is online, the API is down — flip the reachability flag so
  // the overlay renders. (When the browser is offline, the OfflineBanner
  // already covers it; this flag stays clean.)
  //
  // Any non-FETCH_ERROR result — including 4xx and 5xx HTTP responses —
  // means the server is alive enough to reply, so we mark it reachable.
  // This auto-recovers the overlay the instant the API comes back.
  const isOfflineFromBrowser =
    typeof navigator !== 'undefined' && navigator.onLine === false;
  // An *aborted* request also surfaces as FETCH_ERROR, but it says nothing
  // about server health — it just means the caller went away (component
  // unmounted, or logout's `resetApiState()` cancelled every live query). If we
  // treated that as an outage, logging out would flash the "connection lost"
  // overlay every time. Detect it via the request's own abort signal, with the
  // error message as a fallback, and leave reachability untouched.
  const wasAborted =
    api.signal?.aborted === true ||
    (result.error?.status === 'FETCH_ERROR' &&
      typeof result.error.error === 'string' &&
      /abort/i.test(result.error.error));
  if (result.error?.status === 'FETCH_ERROR') {
    if (!isOfflineFromBrowser && !wasAborted) {
      api.dispatch(markUnreachable());
    }
  } else {
    // Either success or an HTTP error (which still means we reached the
    // server). Clear the overlay.
    api.dispatch(markReachable());
  }

  // E11 — HTTP 426 Upgrade Required. The API tells us this client is too
  // old to talk to the current server (breaking schema change). Flip a
  // Redux flag; the UpdateRequiredModal renders the blocking modal. Once
  // set there's no way back — the user has to reload.
  if (result.error?.status === 426) {
    const data = result.error.data as
      | { client_version?: string; min_server_version?: string }
      | undefined;
    api.dispatch(
      markRequired({
        clientVersion: data?.client_version ?? null,
        minServerVersion: data?.min_server_version ?? null,
      }),
    );
    return result;
  }

  if (result.error?.status === 401) {
    // Don't try to refresh the refresh call itself — that's an infinite loop.
    const isRefreshCall =
      typeof args !== 'string' && typeof args.url === 'string' && args.url.includes('/auth/refresh');
    if (!isRefreshCall) {
      const newAccess = await refreshAccessToken();
      if (newAccess) {
        const authStatus = (api.getState() as AppState).auth.status;
        if (authStatus === 'unauthenticated' || api.signal.aborted) {
          return result;
        }
        api.dispatch(authActions.setAccessToken(newAccess));
        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        // Refresh failed → kill local credentials. The middleware will redirect
        // to /login on the next protected-route navigation.
        api.dispatch(authActions.clearCredentials());
      }
    }
  }

  return result;
};
